/**
 * Componente de diagnóstico de permissões do token Meta WABA
 * Executa 3 testes na Graph API e exibe resultado visual
 * 
 * @version 1.0.0
 * @date 2026-02-27
 */

import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  ShieldCheck, 
  CheckCircle2, 
  XCircle, 
  Loader2,
  AlertTriangle,
  User,
  Building2,
  Phone
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

// Logger condicional para este componente
const logger = createLogger('MetaWabaPermissionDiagnostic');

// ============================================================================
// INTERFACES
// ============================================================================

interface MetaWabaPermissionDiagnosticProps {
  inboxId: string;
  hasCredentials: boolean;
}

/** Estrutura de resultado de cada teste individual */
interface TestResult {
  success: boolean;
  error?: string | null;
  [key: string]: unknown;
}

/** Estrutura completa do diagnóstico retornado pela Edge Function */
interface DiagnosticResult {
  diagnosis: string;
  recommendation: string;
  response_time_ms: number;
  tests: {
    test1_token_identity: TestResult & {
      token_type: 'SYSTEM' | 'USER' | 'UNKNOWN';
      owner: { id: string; name: string } | null;
    };
    test2_waba_access: TestResult & {
      waba_accessible: boolean;
      waba_match: boolean;
      waba_count: number;
      configured_waba_id: string | null;
    };
    test3_phone_access: TestResult & {
      phone_accessible: boolean;
      has_messaging_fields: boolean;
      phone_details: Record<string, unknown> | null;
    };
  };
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function MetaWabaPermissionDiagnostic({ 
  inboxId, 
  hasCredentials 
}: MetaWabaPermissionDiagnosticProps) {
  // Estados locais
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<DiagnosticResult | null>(null);

  /**
   * Executa o diagnóstico de permissões chamando provider-proxy
   */
  const handleRunDiagnostic = async () => {
    if (!hasCredentials) {
      toast.error('Credenciais não configuradas');
      return;
    }

    setIsRunning(true);
    setResult(null);

    try {
      logger.info('Iniciando diagnóstico de permissões...');

      const response = await supabase.functions.invoke('provider-proxy', {
        body: {
          inbox_id: inboxId,
          action: 'diagnose_permissions'
        }
      });

      if (response.error) {
        logger.error('Erro ao executar diagnóstico', { error: response.error });
        toast.error('Erro ao executar diagnóstico');
        return;
      }

      const data = response.data;

      if (!data?.success) {
        toast.error(data?.error || 'Falha no diagnóstico');
        return;
      }

      setResult(data.data as DiagnosticResult);
      
      // Toast baseado no diagnóstico
      if (data.data.diagnosis === 'PERMISSOES_OK') {
        toast.success('Permissões verificadas com sucesso!');
      } else {
        toast.warning('Problema detectado nas permissões');
      }

      logger.info('Diagnóstico concluído', { diagnosis: data.data.diagnosis });

    } catch (error) {
      logger.error('Exceção ao executar diagnóstico', { error });
      toast.error('Erro ao executar diagnóstico');
    } finally {
      setIsRunning(false);
    }
  };

  /** Renderiza ícone de sucesso ou falha para cada teste */
  const renderTestIcon = (success: boolean) => {
    return success ? (
      <CheckCircle2 className="h-4 w-4 text-green-600" />
    ) : (
      <XCircle className="h-4 w-4 text-destructive" />
    );
  };

  /** Badge geral do diagnóstico */
  const renderDiagnosisBadge = () => {
    if (!result) return null;

    if (result.diagnosis === 'PERMISSOES_OK') {
      return (
        <Badge className="gap-1 bg-green-500/10 text-green-600 border-green-500/20">
          <CheckCircle2 className="h-3 w-3" />
          Permissões OK
        </Badge>
      );
    }

    return (
      <Badge variant="destructive" className="gap-1">
        <AlertTriangle className="h-3 w-3" />
        Problema detectado
      </Badge>
    );
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Diagnóstico de Permissões
            </CardTitle>
            <CardDescription className="text-xs mt-1">
              Verifica se o token tem as permissões necessárias para enviar mensagens
            </CardDescription>
          </div>
          {renderDiagnosisBadge()}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Botão de execução */}
        {!result && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleRunDiagnostic}
            disabled={isRunning || !hasCredentials}
            className="w-full"
          >
            {isRunning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Executando diagnóstico...
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4 mr-2" />
                Diagnosticar Permissões
              </>
            )}
          </Button>
        )}

        {/* Resultados dos testes */}
        {result && (
          <div className="space-y-3">
            {/* Teste 1: Tipo do Token */}
            <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
              {renderTestIcon(result.tests.test1_token_identity.success && result.tests.test1_token_identity.token_type === 'SYSTEM')}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <User className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">Tipo do Token</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {result.tests.test1_token_identity.success
                    ? `${result.tests.test1_token_identity.token_type === 'SYSTEM' ? 'System User' : 'User comum'} — ${result.tests.test1_token_identity.owner?.name || 'Desconhecido'}`
                    : result.tests.test1_token_identity.error || 'Token inválido'
                  }
                </p>
              </div>
            </div>

            {/* Teste 2: Acesso à WABA */}
            <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
              {renderTestIcon(result.tests.test2_waba_access.success && result.tests.test2_waba_access.waba_match)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">Acesso à WABA</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {result.tests.test2_waba_access.success
                    ? result.tests.test2_waba_access.waba_match
                      ? `WABA acessível (${result.tests.test2_waba_access.waba_count} encontrada${result.tests.test2_waba_access.waba_count > 1 ? 's' : ''})`
                      : `WABA configurada não encontrada entre ${result.tests.test2_waba_access.waba_count} disponíve${result.tests.test2_waba_access.waba_count > 1 ? 'is' : 'l'}`
                    : result.tests.test2_waba_access.error || 'Sem acesso'
                  }
                </p>
              </div>
            </div>

            {/* Teste 3: Acesso ao Número */}
            <div className="flex items-start gap-3 p-3 rounded-md bg-muted/50">
              {renderTestIcon(result.tests.test3_phone_access.success && result.tests.test3_phone_access.has_messaging_fields)}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-sm font-medium">Acesso ao Número</span>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {result.tests.test3_phone_access.success
                    ? result.tests.test3_phone_access.has_messaging_fields
                      ? `Número verificado — ${(result.tests.test3_phone_access.phone_details as Record<string, string>)?.display_phone_number || 'OK'}`
                      : 'Número acessível mas sem campos de envio (verificação pendente?)'
                    : result.tests.test3_phone_access.error || 'Sem acesso ao número'
                  }
                </p>
              </div>
            </div>

            {/* Recomendação */}
            {result.diagnosis !== 'PERMISSOES_OK' && (
              <Alert className="border-destructive/50 bg-destructive/10">
                <AlertTriangle className="h-4 w-4 text-destructive" />
                <AlertDescription className="text-sm">
                  <span className="font-medium">Recomendação:</span>{' '}
                  {result.recommendation}
                </AlertDescription>
              </Alert>
            )}

            {result.diagnosis === 'PERMISSOES_OK' && (
              <Alert className="border-green-500/50 bg-green-500/10">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-sm text-green-700">
                  {result.recommendation}
                </AlertDescription>
              </Alert>
            )}

            {/* Tempo de resposta e botão para re-executar */}
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                Concluído em {result.response_time_ms}ms
              </span>
              <Button
                variant="ghost"
                size="xs"
                onClick={handleRunDiagnostic}
                disabled={isRunning}
              >
                {isRunning ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  'Executar novamente'
                )}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
